<template>
  <div class="verbals-page">
    <header class="verbals-hero">
      <div>
        <div class="verbals-kicker">AFFISE VERBALS</div>
        <h1>Verbals Manager</h1>
      </div>
      <a class="verbals-back" href="/apps/script-tags/data-manager">Back to data manager</a>
    </header>

    <section class="verbals-board">
      <form @submit="save">
        <div class="verbals-grid verbals-grid--head">
          <div>ID</div>
          <div>Code</div>
          <div class="verbals-col-toggle">General Verbal</div>
          <div>Actions</div>
        </div>

        <div
          v-for="row of rows"
          :key="row.id"
          class="verbals-grid verbals-grid--row"
          :class="{ 'is-editing': row.edit }"
        >
          <div><span class="verbals-id">#{{ row.id }}</span></div>

          <div>
            <div v-if="!row.edit" class="verbals-mono">{{ row.code }}</div>
            <b-form-input
              v-else
              class="verbals-input"
              type="text"
              v-model.trim="row.code"
              placeholder="Code"
            />
          </div>

          <div class="verbals-col-toggle">
            <b-form-checkbox
              switch
              v-model="row.is_general"
              :disabled="!row.edit || !isAdmin"
            ></b-form-checkbox>
          </div>

          <div class="verbals-actions">
            <template v-if="!row.edit">
              <button
                v-if="isAdmin"
                class="verbals-btn verbals-btn--neutral"
                type="button"
                @click="editRow(row.id)"
              >
                Edit
              </button>
              <button
                v-if="isAdmin"
                class="verbals-btn verbals-btn--danger"
                type="button"
                @click="deleteEntity(row.id)"
              >
                Delete
              </button>
            </template>
            <template v-else>
              <button class="verbals-btn verbals-btn--ok" type="button" @click="saveEditRow(row.id)">
                Save
              </button>
              <button class="verbals-btn verbals-btn--neutral" type="button" @click="cancelEditRow(row.id)">
                Cancel
              </button>
            </template>
          </div>
        </div>

        <div v-if="isAdmin && showNewRow" class="verbals-grid verbals-grid--row verbals-grid--new">
          <div><span class="verbals-id">NEW</span></div>
          <div>
            <b-form-input
              class="verbals-input"
              type="text"
              v-model.trim="newRow.code"
              placeholder="Code"
            />
          </div>
          <div class="verbals-col-toggle">
            <b-form-checkbox switch v-model="newRow.is_general"></b-form-checkbox>
          </div>
          <div class="verbals-actions">
            <button class="verbals-btn verbals-btn--ok" type="submit">Add</button>
          </div>
        </div>
      </form>
    </section>
  </div>
</template>

<script>
export default {
  data() {
    return {
      newRow: {
        id: 0,
        code: '',
        is_general: false,
      },
      rows: [],
      showNewRow: true,
      isAdmin: true,
      editSnapshots: {},
    }
  },
  async created() {
    const permList = await this.$store.state.user.perms
    if (Object.values(permList).indexOf('bundles.admin') > -1) {
      this.isAdmin = true
    }
    this.getListData()
  },
  methods: {
    normalizeRow(row) {
      row.edit = false
      row.is_general = row.is_general === 1 || row.is_general === true
      return row
    },
    async save(e) {
      e.preventDefault()
      if (!this.newRow.code) {
        this.notifyError('Code is required')
        return
      }
      const data = await this.$http.post(this.resources.Management.saveDmVerbal, {
        ...this.newRow,
        is_general: this.newRow.is_general ? 1 : 0,
      })
      if (data.body.res !== 'OK') {
        this.notifyError(data.body.msg || 'Failed to save verbal')
        return
      }
      this.newRow.code = ''
      this.newRow.is_general = false
      this.getListData()
    },
    async getListData() {
      const data = await this.$http.post(this.resources.Management.getDmVerbals, {})
      if (data.body.res !== 'OK') {
        this.notifyError(data.body.msg || 'Failed to load verbals')
        return
      }
      this.rows = (data.body.data || []).map((row) => this.normalizeRow(row))
    },
    async deleteEntity(id) {
      id = parseInt(id)
      const data = await this.$http.post(this.resources.Management.deleteDmVerbal, { id })
      if (data.body.res !== 'OK') {
        this.notifyError(data.body.msg || 'Failed to delete verbal')
        return
      }
      this.getListData()
    },
    editRow(id) {
      this.showNewRow = false
      this.rows.forEach((row) => {
        if (row.id === id) {
          this.editSnapshots[id] = {
            code: row.code,
            is_general: row.is_general,
          }
          row.edit = true
        } else {
          if (row.edit && this.editSnapshots[row.id]) {
            Object.assign(row, this.editSnapshots[row.id])
            delete this.editSnapshots[row.id]
          }
          row.edit = false
        }
      })
    },
    cancelEditRow(id) {
      this.showNewRow = true
      this.rows.forEach((row) => {
        if (row.id === id) {
          if (this.editSnapshots[id]) {
            Object.assign(row, this.editSnapshots[id])
            delete this.editSnapshots[id]
          }
          row.edit = false
        }
      })
    },
    async saveEditRow(id) {
      const editRowData = this.rows.find((row) => row.id === id)
      if (!editRowData) return
      if (!editRowData.code) {
        this.notifyError('Code is required')
        return
      }
      const data = await this.$http.post(this.resources.Management.saveDmVerbal, {
        id: editRowData.id,
        code: editRowData.code,
        is_general: editRowData.is_general ? 1 : 0,
      })
      if (data.body.res !== 'OK') {
        this.notifyError(data.body.msg || 'Failed to save verbal')
        return
      }
      this.showNewRow = true
      delete this.editSnapshots[id]
      this.getListData()
    },
  },
}
</script>

<style scoped>
.verbals-page {
  min-height: 100%;
  padding: 24px;
  color: #0f172a;
  background: #f8fafc;
}

.verbals-hero {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 18px;
}

.verbals-kicker {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .5px;
  background: rgba(15, 23, 42, 0.05);
  border: 1px solid rgba(15, 23, 42, 0.12);
}

.verbals-hero h1 {
  margin: 8px 0 4px;
  font-size: 32px;
  line-height: 1.05;
  font-weight: 800;
}

.verbals-back {
  color: #0ea5e9;
  text-decoration: none;
  border: 1px solid rgba(14, 165, 233, 0.28);
  padding: 8px 14px;
  border-radius: 999px;
  background: rgba(14, 165, 233, 0.08);
}

.verbals-board {
  border-radius: 20px;
  padding: 14px;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(15, 23, 42, 0.10);
  box-shadow: 0 18px 60px rgba(15, 23, 42, 0.10);
  backdrop-filter: blur(10px);
}

.verbals-grid {
  display: grid;
  grid-template-columns: 68px minmax(220px, 1fr) 120px 185px;
  column-gap: 12px;
  align-items: center;
}

.verbals-grid--head {
  color: #475569;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: .4px;
  text-transform: uppercase;
  padding: 8px 14px 12px;
}

.verbals-grid--row {
  margin-bottom: 10px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid rgba(15, 23, 42, 0.10);
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
}

.verbals-grid--row:hover {
  border-color: rgba(14, 165, 233, 0.35);
  box-shadow: 0 14px 32px rgba(14, 165, 233, 0.10);
}

.verbals-grid--row.is-editing {
  border-color: rgba(14, 165, 233, 0.65);
  background: rgba(240, 249, 255, 0.95);
  box-shadow: 0 0 0 1px rgba(14, 165, 233, 0.18) inset, 0 16px 34px rgba(14, 165, 233, 0.12);
}

.verbals-grid--new {
  border-style: dashed;
  border-color: rgba(34, 197, 94, 0.45);
  background: rgba(240, 253, 244, 0.92);
}

.verbals-id {
  display: inline-flex;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  background: rgba(15, 23, 42, 0.06);
}

.verbals-col-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.verbals-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.verbals-actions {
  display: flex;
  gap: 8px;
}

.verbals-btn {
  border: 0;
  border-radius: 10px;
  padding: 7px 12px;
  font-weight: 700;
  color: #fff;
}

.verbals-btn--ok {
  background: linear-gradient(135deg, #10b981, #22c55e);
}

.verbals-btn--neutral {
  background: linear-gradient(135deg, #64748b, #475569);
}

.verbals-btn--danger {
  background: linear-gradient(135deg, #ef4444, #dc2626);
}

:deep(.verbals-input.form-control) {
  min-height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.55);
  background: rgba(255, 255, 255, 1);
}

:deep(.verbals-input.form-control:focus) {
  border-color: rgba(14, 165, 233, 0.85);
  box-shadow: 0 0 0 0.18rem rgba(14, 165, 233, 0.22);
}

:deep(.custom-control-input:checked ~ .custom-control-label::before) {
  background-color: #10b981;
  border-color: #10b981;
}

@media (max-width: 900px) {
  .verbals-board {
    overflow-x: auto;
  }

  .verbals-grid {
    min-width: 640px;
  }
}
</style>
